import { DefaultButton, Link, mergeStyleSets, Pivot, PivotItem, useTheme } from "@fluentui/react";
import clsx from "clsx";
import {
  addMinutes,
  areIntervalsOverlapping,
  eachMinuteOfInterval,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
} from "date-fns";
import { closestTo } from "date-fns/esm";
import { isNil } from "lodash";
import * as React from "react";
import { createRef, FunctionComponent, RefObject, useCallback, useEffect, useState } from "react";
import { useOutlook } from "@hooks";
import { BookingsAndSpaceAvailabilities, Space, SpaceStatus } from "@graphql";
import BackHeader from "../../shared/back-header/BackHeader";
import ImagePlaceholder from "../../shared/image-placeholder/ImagePlaceholder";
import Amenities from "../amenities/Amenities";
import ScheduleSlot, { SlotDetails, SlotStatus } from "../schedule-slot/ScheduleSlot";

interface Props {
  spaceAvailability: BookingsAndSpaceAvailabilities;
  start: Date;
  end: Date;
  onAddSpaceToEvent: (space: Space) => void;
  onBackClick: () => void;
}

const intervalMinutes = 15;

const SpaceDetails: FunctionComponent<Props> = (props: Props) => {
  const { spaceAvailability, onAddSpaceToEvent, onBackClick, start, end } = props;
  const [slots, setSlots] = useState<SlotDetails[]>([]);
  const [slotRefs, setSlotRefs] = useState<RefObject<HTMLDivElement>[]>([]);
  const { updateEventEnd, updateEventStart } = useOutlook();

  const theme = useTheme();
  const classNames = mergeStyleSets({
    itemImage: {
      flexShrink: 0,
    },
    itemContent: {
      marginLeft: 10,
      overflow: "hidden",
      flexGrow: 1,
    },
    itemName: [
      theme.fonts.xLarge,
      {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        paddingTop: 10,
      },
    ],
    addButton: {
      backgroundColor: "#F1F1F1",
      width: "100%",
      border: 0,
    },
    addToSpace: {
      textAlign: "center",
      padding: 10,
      marginRight: 10,
    },
    openInSpaces: {
      "&:hover": { background: "#F8F6FE" },
    },
    openInSpacesLink: {
      "&:hover": { textDecoration: "none" },
    },
  });

  // works out the status of a slot.
  const calculateSlotStatus = useCallback(
    (slots: SlotDetails[]) => {
      console.log("calculating slot status", spaceAvailability.roomBookings);
      if (spaceAvailability.roomStatus.status === SpaceStatus.Unavailable) {
        slots.map((slot) => (slot.status = SlotStatus.Unavailable));
      } else {
        //  loop through all bookings and find slots that sit within it.
        const foundSlots = spaceAvailability.roomBookings.flatMap((booking) =>
          slots
            .filter((slot) =>
              areIntervalsOverlapping(
                { start: slot.start, end: slot.end },
                { start: new Date(booking.startTime), end: new Date(booking.endTime) },
              ),
            )
            .map((booking) => booking.index),
        );
        slots.map(
          (slot) =>
            (slot.status = foundSlots.some((found) => found === slot.index) ? SlotStatus.Booked : SlotStatus.Available),
        );
        // mark slots requested, if not booked already.
        const requestedSlots = slots
          .filter((slot) => areIntervalsOverlapping({ start: slot.start, end: slot.end }, { start: start, end: end }))
          .map((booking) => booking.index);
        slots
          .filter((slot) => requestedSlots.some((rs) => slot.index === rs))
          .map((s) => (s.status = s.status === SlotStatus.Available ? SlotStatus.Selected : s.status));

        // mark historical slots
        const now = new Date();
        slots.filter((slot) => isBefore(slot.start, now)).forEach((slot) => (slot.status = SlotStatus.Historic));
      }
    },
    [end, spaceAvailability.roomBookings, spaceAvailability.roomStatus, start],
  );

  // builds slot intervals
  useEffect(() => {
    if (isAfter(start, end)) {
      return undefined;
    }
    const intervals = eachMinuteOfInterval(
      {
        start: startOfDay(start),
        end: endOfDay(end),
      },
      { step: intervalMinutes },
    );
    const slots: SlotDetails[] = intervals.map((interval: Date, index: number) => {
      const end = addMinutes(interval, intervalMinutes - 1);
      return {
        index,
        start: interval,
        end: end,
        status: SlotStatus.Available,
      };
    });
    calculateSlotStatus(slots);
    setSlots(slots);
    // create slot references.
    setSlotRefs(slots.map(() => createRef<HTMLDivElement>()));
  }, [calculateSlotStatus, end, start]);

  // when start changes, move to ref.
  useEffect(() => {
    if (isNil(slots) || isNil(start)) {
      return undefined;
    }
    const closest = closestTo(
      start,
      slots.map((slot) => slot.start),
    );
    const slot = slots.find((slot) => isEqual(slot.start, closest));
    if (!isNil(slot)) {
      slotRefs[slot.index === 0 ? slot.index : slot.index - 1].current.scrollIntoView();
    }
  }, [slotRefs, slots, start]);

  const slotClicked = (slot: SlotDetails) => {
    if (slot.status === SlotStatus.Available && isBefore(slot.start, start)) {
      console.log("slot before");
      updateEventStart(slot.start);
    } else if (slot.status === SlotStatus.Available || slot.status == SlotStatus.Selected) {
      updateEventEnd(addMinutes(slot.start, intervalMinutes));
    }
  };

  const onRenderCell = (slot: SlotDetails, index: number | undefined) => {
    return (
      <div ref={slotRefs[index]} key={`slot-${index}`}>
        <ScheduleSlot key={`schedule-slot-${index}`} slot={slot} index={index} onClick={slotClicked} />
      </div>
    );
  };

  return (
    <>
      <BackHeader title={"Space Details"} onBackClick={onBackClick} />
      <div>
        {/* <Image className={classNames.itemImage} src={spaceAvailability.space.thumbnail} width={50} height={50} imageFit={ImageFit.cover} /> */}
        <div className={classNames.itemContent}>
          <ImagePlaceholder height={200} width={"97%"} src={spaceAvailability.space?.image}></ImagePlaceholder>
          <div className={classNames.itemName}>{spaceAvailability.space.name}</div>
          <div>Selected time</div>
          <div style={{ fontWeight: "bold" }}>
            {format(start, "HH:mm")} - {format(end, "HH:mm")}
          </div>
          <div
            className={clsx(classNames.addToSpace, {
              [classNames.openInSpaces]: spaceAvailability.space.approvalsRequired,
            })}
          >
            {!spaceAvailability.space.approvalsRequired &&
              spaceAvailability.roomStatus.status !== SpaceStatus.Unavailable && (
                <DefaultButton
                  toggle
                  text={spaceAvailability.space.addedToOutlook ? "Added to event" : "Add space to event"}
                  disabled={!spaceAvailability.space.bookable}
                  onClick={() => onAddSpaceToEvent(spaceAvailability.space)}
                  iconProps={spaceAvailability.space.addedToOutlook ? { iconName: "CheckMark" } : { iconName: "Add" }}
                  className={clsx(classNames.addButton)}
                />
              )}
            {spaceAvailability.space.approvalsRequired && (
              <Link href="https://ricohspaces.app" target="_blank" className={classNames.openInSpacesLink}>
                <div style={{ color: "#5A12DE", width: "100%" }}>Open RICOH Spaces</div>
              </Link>
            )}
          </div>
          <div>
            <Pivot>
              <PivotItem headerText="Room schedule">
                <div>
                  {slots && slots.length > 0 && (
                    <div style={{ height: 500, position: "relative", overflow: "scroll" }}>
                      {slots.map((slot, index) => onRenderCell(slot, index))}
                    </div>
                  )}
                </div>
              </PivotItem>
              <PivotItem headerText="Amenities">
                <Amenities spaceProperties={spaceAvailability.space.amenities} />
              </PivotItem>
            </Pivot>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpaceDetails;
